import SchoolWorkspace from '../components/SchoolWorkspace.jsx';
// src/pages/AccountPage.jsx
//
// Sprint 2c-2b-2: Account dashboard page assembling five tabs
// (overview, tutor-review, my-bookings, my-purchases, edit-profile)
// with the session-storage initial-tab one-shot override preserved.
// Migrated verbatim from prototype/between-the-lines-v2.html lines 8526-9181.
//
// Migration adjustments per Sprint 2c-1 + Sprint 2c-2b-1 precedent:
//   - Prototype page-state setter prop dropped; routing now uses the
//     router navigate hook (eight conversion sites total).
//   - Per-pack selection setter prop dropped; two internal call sites
//     commented out with Sprint 2d wiring TODOs (Progress card Continue
//     Learning button + Study Pack list Open button).
//   - The per-pack selection setter is no longer passed as a prop to
//     the My Purchases panel — that panel's current signature already
//     dropped the prop in Sprint 2c-2b-1.
//
// State-prop pattern (Sprint 2c-1 convention; Sprint 2c-2b-1 V1 amendment
// confirms direct ({ state }) prop, not the outlet context hook).
//
// Tab system: inline tab logic per ChatGPT cross-review Decision 3 Option (a).
//   - No AccountTabs sub-component extraction.
//   - Tab state lives in this file as a single useState.
//   - The session-storage one-shot key 'btl_account_initial_tab' is consumed
//     on mount then immediately removed (pattern preserved from prototype L8536).
//
// Routes navigated to: 8 sites across 8 distinct destinations
//   /set-text, /skills-workshop, /literature-study, /pack-placeholder
//   /dashboard, /checkout, /booking, /home
//   (see §2.2 navigation table in spec for exact site mapping)

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    IconArrowRight, IconBookOpen,
    IconCalendar, IconCheck, IconClock,
    IconLogOut, IconTrophy, IconX
} from '../components/icons.jsx';
import { DEFAULT_ACCOUNT } from '../config/account.js';
import { formatSubDate, daysUntil } from '../utils/dateHelpers.js';
import { canAccessPack, getSubStatus, getSubscription } from '../utils/subscriptionHelpers.js';
import { getPackType } from '../utils/packHelpers.js';
import { getReferenceCode } from '../utils/tutorReviewHelpers.js';
import PaymentInstructionsBlock from '../components/PaymentInstructionsBlock.jsx';
import MyPurchasesPanel from '../components/MyPurchasesPanel.jsx';
import MyTutorReviewPanel from '../components/MyTutorReviewPanel.jsx';
import MyEssaysPanel from '../components/MyEssaysPanel.jsx';
import AccountSavedPanel from '../components/AccountSavedPanel.jsx';
import { HelpFeedbackTrigger } from '../components/HelpFeedback.jsx';
import MyBookingsPanel from '../components/MyBookingsPanel.jsx';
import { useAuth } from '../auth/useAuth.js';
import { useEntitlements } from '../storage/useEntitlements.js';
import { usePendingPurchases } from '../storage/usePendingPurchases.js';

export const AccountPage = ({ state }) => {
    const navigate = useNavigate();
    const { signOut } = useAuth();
    const account = { ...DEFAULT_ACCOUNT, ...(state.accountContent || {}) };
    // WP-3: ownership + pending purchases come from Firestore, not localStorage.
    const { activePackIds } = useEntitlements();
    const { pending: firestorePending } = usePendingPurchases();
    // Ownership comes from real entitlements only (no per-id always-owned hardcode).
    const ownedPacks = state.packs.filter(p => activePackIds.has(p.id));
    const [tempExamDate, setTempExamDate] = useState(state.examDate);
    const [dateEditing, setDateEditing] = useState(false);
    const [schedulePickerOpen, setSchedulePickerOpen] = useState(false);
    const serviceReturn = new URLSearchParams(window.location.search).get('servicePayment');
    const returnTab = new URLSearchParams(window.location.search).get('tab');
    const [activeTab, setActiveTab] = useState(['my-bookings', 'tutor-review'].includes(returnTab) ? returnTab : 'overview');

    // Honor a one-shot tab override set by another page (e.g. PackPurchasePayMePage
    // jumps here after submission and wants to land on My Purchases).
    useEffect(() => {
        const initialTab = sessionStorage.getItem('btl_account_initial_tab');
        if (initialTab) {
            setActiveTab(initialTab);
            sessionStorage.removeItem('btl_account_initial_tab');
        }
    }, []);

    const [profileForm, setProfileForm] = useState({
        name: state.user?.name || '',
        school: state.user?.school || '',
        yearGroup: state.user?.yearGroup || '',
        targetGrade: state.user?.targetGrade || '',
    });
    const [profileSaved, setProfileSaved] = useState(false);
    const [emailChangePending, setEmailChangePending] = useState(false);

    // Calc days to exam
    const daysToExam = useMemo(() => {
        if (!state.examDate) return null;
        const examTs = new Date(state.examDate).getTime();
        const nowTs = Date.now();
        const diff = Math.ceil((examTs - nowTs) / (1000 * 60 * 60 * 24));
        return diff;
    }, [state.examDate]);

    const saveProfile = () => {
        state.setUser({ ...state.user, name: profileForm.name, school: profileForm.school, yearGroup: profileForm.yearGroup, targetGrade: profileForm.targetGrade });
        setProfileSaved(true);
        setTimeout(() => setProfileSaved(false), 3000);
    };

    return (
        <div className="page-fade-in min-h-[calc(100vh-4rem)] bg-midnight-bg paper-texture">
            <div className="max-w-6xl mx-auto px-4 md:px-8 py-10 md:py-16">
                {/* Header */}
                <div className="mb-6">
                    <div className="font-display text-midnight-accent text-xs mb-2 font-semibold">{account.welcomeTagline}</div>
                    <h1 className="font-display text-3xl md:text-5xl text-midnight-text mb-2 leading-tight">{state.user?.name}</h1>
                    <p className="font-serif italic text-midnight-muted text-lg">{state.user?.email}</p>
                    <div className="mt-3"><HelpFeedbackTrigger /></div>
                </div>

                {/* Tab bar */}
                {(() => {
                    const userEmail = state.user?.email;
                    const unreadCount = Object.values(state.submissions || {})
                        .filter(s => (!userEmail || s.userEmail === userEmail) && s.status === 'reviewed' && !s.viewedByStudent).length;
                    const newQuotesCount = Object.values(state.bookings || {})
                        .filter(b => (!userEmail || b.userEmail === userEmail) && b.status === 'confirmed').length;
                    // WP-3: pending count from the live Firestore listener (status 'pending').
                    const newPurchasesCount = firestorePending.filter(p => p.status === 'pending').length;
                    return (
                        // Mobile: 7 tabs overflow a phone width, so the row scrolls horizontally
                        // (whitespace-nowrap + flex-shrink-0 tabs + tighter px-3) and a right-edge
                        // fade (md:hidden) signals there's more. At md+ all tabs fit, so the row is
                        // identical to the original full-width bar (px-6, no scroll/fade).
                        <div className="relative mb-8">
                        <div className="flex border-b border-midnight-accent/30 overflow-x-auto no-scrollbar whitespace-nowrap md:overflow-visible">
                            {[['overview','Overview'],['tutor-review','Tutor Review'],['my-essays','My Essays'],['saved','Notes & Saved'],['my-bookings','My Bookings'],['my-purchases','My Purchases'],['school','School'],['edit-profile','Edit Profile']].map(([id,label]) => (
                                <button key={id} onClick={() => setActiveTab(id)}
                                    className={`flex-shrink-0 px-3 md:px-5 py-3 font-display text-xs transition-colors relative font-semibold ${activeTab === id ? 'text-midnight-accent' : 'text-midnight-muted hover:text-midnight-text'}`}>
                                    <span className="inline-flex items-center gap-2">
                                        {label}
                                        {id === 'tutor-review' && unreadCount > 0 && (
                                            <span className="inline-block w-2 h-2 rounded-full bg-midnight-accent shadow-[0_0_6px_rgba(201,169,97,0.8)]" title={`${unreadCount} new tutor review${unreadCount === 1 ? '' : 's'}`}></span>
                                        )}
                                        {id === 'my-bookings' && newQuotesCount > 0 && (
                                            <span className="inline-block w-2 h-2 rounded-full bg-midnight-accent shadow-[0_0_6px_rgba(201,169,97,0.8)]" title={`${newQuotesCount} new quote${newQuotesCount === 1 ? '' : 's'}`}></span>
                                        )}
                                        {id === 'my-purchases' && newPurchasesCount > 0 && (
                                            <span className="inline-block w-2 h-2 rounded-full bg-amber-400 shadow-[0_0_6px_rgba(245,158,11,0.8)]" title={`${newPurchasesCount} pending purchase${newPurchasesCount === 1 ? '' : 's'}`}></span>
                                        )}
                                    </span>
                                    {activeTab === id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-midnight-accent"></div>}
                                </button>
                            ))}
                        </div>
                        {/* Right-edge scroll cue, mobile only (fades to the page bg). */}
                        <div className="btl-edge-fade pointer-events-none absolute top-0 bottom-0 right-0 w-10 bg-gradient-to-l from-midnight-bg to-transparent md:hidden"></div>
                        </div>
                    );
                })()}

                {serviceReturn && <p role="status" className="border border-midnight-accent/30 p-4 mb-5 text-midnight-text">{serviceReturn === 'returned' ? 'Your payment is being checked. The request below will show Paid when confirmation arrives. If you were charged, do not pay again.' : 'You returned from card checkout. You can resume the same checkout below. If you see a charge, contact Danielle before paying again.'}</p>}
                {activeTab === 'overview' && (<div className="grid md:grid-cols-3 gap-6">
                    {/* LEFT column — Profile & Exam & Progress */}
                    <div className="space-y-5">
                        {/* Profile card */}
                        <div className="border border-midnight-accent/20 bg-midnight-page/60 p-5 decorative-corner text-midnight-accent">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-12 h-12 rounded-full bg-midnight-accent/20 border border-midnight-accent flex items-center justify-center">
                                    <span className="font-display text-lg text-midnight-accent">{state.user?.name?.[0]?.toUpperCase() || 'U'}</span>
                                </div>
                                <div>
                                    <div className="font-display text-midnight-text text-sm font-semibold">{state.user?.name}</div>
                                    <div className="font-serif italic text-midnight-muted text-xs">{state.user?.email}</div>
                                </div>
                            </div>
                            <div className="h-px bg-midnight-accent/30 my-3"></div>
                            <div className="flex justify-between items-center text-sm font-serif">
                                <span className="text-midnight-muted">Member since</span>
                                <span className="text-midnight-accent">{state.user?.joined ? new Date(state.user.joined).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) : '—'}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm font-serif mt-2">
                                <span className="text-midnight-muted">Packs owned</span>
                                <span className="text-midnight-accent font-display">{ownedPacks.length}</span>
                            </div>
                        </div>

                        {/* Exam Countdown */}
                        <div className="border border-midnight-accent/20 bg-midnight-page/60 p-5 decorative-corner text-midnight-accent">
                            <div className="flex items-center gap-2 mb-3">
                                <IconClock className="w-4 h-4 text-midnight-accent" />
                                <h3 className="font-display text-midnight-accent text-xs font-semibold">Exam Countdown</h3>
                            </div>
                            {state.examDate && !dateEditing ? (
                                <>
                                    <div className="text-center py-3">
                                        <div className="font-display text-midnight-accent text-4xl md:text-5xl mb-1">
                                            {daysToExam > 0 ? daysToExam : 0}
                                        </div>
                                        <div className="font-serif italic text-midnight-muted text-sm">
                                            {daysToExam > 0 ? 'days to go' : daysToExam === 0 ? 'today' : 'exam passed'}
                                        </div>
                                    </div>
                                    <div className="text-center text-midnight-muted font-serif italic text-xs mb-3">
                                        {new Date(state.examDate).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                                    </div>
                                    <div className="space-y-2">
                                        <button onClick={() => { setTempExamDate(state.examDate); setDateEditing(true); }}
                                            className="w-full py-2 border border-midnight-accent/40 text-midnight-accent font-display text-xs hover:bg-midnight-accent/10 transition-colors font-semibold">
                                            Change Date
                                        </button>
                                        {state.examDates.length > 0 && (
                                            <button onClick={() => setSchedulePickerOpen(true)}
                                                className="w-full py-2 border border-midnight-accent/30 text-midnight-muted font-display text-xs hover:text-midnight-accent hover:border-midnight-accent/60 transition-colors font-semibold">
                                                Select from exam schedule
                                            </button>
                                        )}
                                    </div>
                                </>
                            ) : dateEditing ? (
                                <>
                                    <p className="font-serif italic text-midnight-muted text-sm text-center mb-3">Set your exam date</p>
                                    <input type="date" value={tempExamDate} onChange={(e) => setTempExamDate(e.target.value)}
                                        className="w-full bg-midnight-pageDark border border-midnight-accent/30 px-3 py-2 text-midnight-text font-serif focus:outline-none focus:border-midnight-accent mb-2" />
                                    <div className="grid grid-cols-2 gap-2">
                                        <button onClick={() => setDateEditing(false)} className="py-2 border border-midnight-muted/40 text-midnight-muted font-display text-xs font-semibold">Cancel</button>
                                        <button onClick={() => { state.setExamDate(tempExamDate); setDateEditing(false); }}
                                            className="py-2 bg-midnight-accent text-midnight-pageDark font-display text-xs hover:bg-midnight-accentLight font-semibold">Save</button>
                                    </div>
                                    {state.examDates.length > 0 && (
                                        <button onClick={() => setSchedulePickerOpen(true)}
                                            className="mt-2 w-full py-2 border border-midnight-accent/30 text-midnight-muted font-display text-xs hover:text-midnight-accent hover:border-midnight-accent/60 transition-colors font-semibold">
                                            Select from exam schedule
                                        </button>
                                    )}
                                </>
                            ) : (
                                <>
                                    <p className="font-serif italic text-midnight-muted text-sm text-center mb-3">{account.examCountdownDescription}</p>
                                    <div className="space-y-2">
                                        <button onClick={() => setDateEditing(true)} className="w-full py-2 bg-midnight-accent text-midnight-pageDark font-display text-xs hover:bg-midnight-accentLight transition-colors font-semibold">
                                            Set Date
                                        </button>
                                        {state.examDates.length > 0 && (
                                            <button onClick={() => setSchedulePickerOpen(true)}
                                                className="w-full py-2 border border-midnight-accent/40 text-midnight-accent font-display text-xs hover:bg-midnight-accent/10 transition-colors font-semibold">
                                                Select from exam schedule
                                            </button>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>

                    </div>

                    {/* RIGHT column — Packs, Sessions (saved sections live in the Notes & Saved tab) */}
                    <div className="md:col-span-2 space-y-5">
                        {/* Study Packs */}
                        <div>
                            <div className="flex items-center gap-4 mb-4">
                                <h3 className="font-display text-midnight-text text-lg">Your Study Packs</h3>
                                <div className="flex-grow h-px bg-midnight-accent/30"></div>
                            </div>
                            {ownedPacks.length === 0 ? (
                                <div className="border border-midnight-accent/20 bg-midnight-page/40 p-8 text-center text-midnight-accent">
                                    <p className="font-serif italic text-midnight-muted mb-4">You haven't unlocked a pack yet. Browse the library to explore available free lessons.</p>
                                    <button onClick={() => navigate('/dashboard')} className="px-5 py-2 bg-midnight-accent text-midnight-pageDark font-display text-xs hover:bg-midnight-accentLight transition-colors font-semibold">
                                        Browse Packs
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {ownedPacks.map(p => {
                                        const status = getSubStatus(state, p.id);
                                        const sub = getSubscription(state, p.id);
                                        const days = daysUntil(sub?.expiryDate);
                                        const badge = status === 'active'
                                            ? { label: 'Active', className: 'border-green-500/50 text-green-400 bg-green-900/20' }
                                            : status === 'expiring'
                                            ? { label: 'Expiring Soon', className: 'border-amber-500/60 text-amber-300 bg-amber-900/20' }
                                            : status === 'expired'
                                            ? { label: 'Expired', className: 'border-red-500/60 text-red-300 bg-red-900/20' }
                                            : null;
                                        return (
                                            <div key={p.id}>
                                                {status === 'expiring' && (
                                                    <div className="border border-amber-500/50 bg-amber-900/20 p-3 mb-2 rounded-sm">
                                                        <p className="font-serif italic text-amber-200 text-xs leading-relaxed">
                                                            Your <span className="font-display not-italic font-semibold text-book-muted text-xs">{p.label}</span> access expires in {days} {days === 1 ? 'day' : 'days'}. You won't be charged automatically.
                                                        </p>
                                                    </div>
                                                )}
                                                {status === 'expired' && (
                                                    <div className="border border-red-500/50 bg-red-900/20 p-3 mb-2 rounded-sm flex items-center gap-3">
                                                        <p className="font-serif italic text-red-200 text-xs leading-relaxed flex-grow">
                                                            Your access has expired. Purchase again to regain access; you have not been charged automatically.
                                                        </p>
                                                        <button onClick={() => { state.setCart([...state.cart, p]); navigate('/checkout'); }}
                                                            className="px-3 py-1.5 bg-red-500/80 text-white font-display text-sm hover:bg-red-500 transition-colors flex-shrink-0 font-medium rounded-full">
                                                            Renew Now
                                                        </button>
                                                    </div>
                                                )}
                                                <div className="border border-midnight-accent/20 bg-midnight-page/40 p-4 flex items-center gap-4 text-midnight-accent">
                                                    <div className="w-10 h-10 bg-midnight-accent/10 border border-midnight-accent/40 flex items-center justify-center flex-shrink-0">
                                                        <IconBookOpen className="w-5 h-5" />
                                                    </div>
                                                    <div className="flex-grow min-w-0">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <div className="font-display text-midnight-text text-sm truncate">{p.label}</div>
                                                            {badge && (
                                                                <span className={`px-3 py-1.5 border font-display text-sm font-medium rounded-full ${badge.className}`}>
                                                                    {badge.label}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="font-serif italic text-midnight-muted text-xs truncate">{p.subLabel}</div>
                                                        {sub?.expiryDate && (
                                                            <div className="font-serif italic text-midnight-muted text-xs mt-1">
                                                                {status === 'expired' ? 'Expired on' : 'Expires:'} {formatSubDate(sub.expiryDate)} · No automatic renewal
                                                            </div>
                                                        )}
                                                    </div>
                                                    <button onClick={() => {
                                                            if (status === 'expired') return;
                                                            // Phase 1.5: per-pack selection-setter for study-app per-pack navigation.
                                                            // Deferred to Phase 1.5 study-app migration sprint when destination routes
                                                            // (set-text, skills-workshop, literature-study, pack-placeholder) consume
                                                            // the pack identifier. This site's navigate call uses pack-type dispatch
                                                            // only; per-pack identity flows through URL parameters at that future time.
                                                            const t = getPackType(p);
                                                            navigate(t === 'set-text' ? '/set-text/' + p.id : t === 'skills-workshop' ? '/skills-workshop' : t === 'literature-study' ? '/literature-study/' + p.id : '/pack-placeholder');
                                                        }}
                                                        disabled={status === 'expired'}
                                                        className={`px-3 py-1.5 border font-display text-sm transition-colors flex items-center gap-1 flex-shrink-0 font-medium rounded-full ${status === 'expired' ? 'border-midnight-muted/30 text-midnight-muted/40 cursor-not-allowed' : 'border-midnight-accent text-midnight-accent hover:bg-midnight-accent hover:text-midnight-pageDark'}`}>
                                                        Open <IconArrowRight className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Upcoming Sessions */}
                        <div>
                            <div className="flex items-center gap-4 mb-4">
                                <h3 className="font-display text-midnight-text text-lg">Upcoming Sessions</h3>
                                <div className="flex-grow h-px bg-midnight-accent/30"></div>
                            </div>
                            <div className="border border-midnight-accent/20 bg-midnight-page/40 p-6 text-center text-midnight-accent">
                                <IconCalendar className="w-8 h-8 mx-auto mb-2 opacity-40" />
                                <p className="font-serif italic text-midnight-muted text-sm mb-3">No sessions booked.</p>
                                <button onClick={() => navigate('/booking')} className="inline-flex items-center gap-2 px-4 py-2 border border-midnight-accent text-midnight-accent font-display text-xs hover:bg-midnight-accent hover:text-midnight-pageDark transition-colors font-semibold">
                                    <IconCalendar className="w-3 h-3" /> Book a Session
                                </button>
                            </div>
                        </div>

                        {/* Personal Statement help (ps-discoverability slice).
                            Additive promo card only -- NO new tab: a student's PS
                            requests already surface under Tutor Review / My Bookings.
                            Links to the landing page (/personal-statement). */}
                        <div className="border border-midnight-accent/30 bg-midnight-page/60 p-5 decorative-corner text-midnight-accent">
                            <div className="font-display text-midnight-accent text-xs mb-2 font-semibold">Personal Statement help</div>
                            <p className="font-serif text-midnight-muted text-sm leading-relaxed mb-4">
                                Applying to university? Danielle reviews UCAS and overseas personal statements
                                line by line, and offers one-to-one admissions consultations.
                            </p>
                            <button onClick={() => navigate('/personal-statement')}
                                className="inline-flex items-center gap-2 px-4 py-2 border border-midnight-accent text-midnight-accent font-display text-xs hover:bg-midnight-accent hover:text-midnight-pageDark transition-colors font-semibold">
                                Learn more <IconArrowRight className="w-3 h-3" />
                            </button>
                        </div>

                        {/* Sign out */}
                        <div className="flex justify-end pt-4">
                            <button onClick={async () => { if (confirm('Sign out?')) { await signOut(); navigate('/home'); } }}
                                className="px-4 py-2 border border-midnight-muted/30 text-midnight-muted font-display text-xs hover:border-red-400 hover:text-red-400 transition-colors flex items-center gap-2 font-semibold">
                                <IconLogOut className="w-3 h-3" /> Sign Out
                            </button>
                        </div>
                    </div>
                </div>)}

                {activeTab === 'tutor-review' && <MyTutorReviewPanel state={state} />}

                {activeTab === 'my-essays' && <MyEssaysPanel />}

                {activeTab === 'saved' && <AccountSavedPanel state={state} />}
                {activeTab === 'school' && <><a className="inline-block border rounded-full px-4 py-2 mb-4" href="/school">Open School workspace →</a><SchoolWorkspace /></>}

                {activeTab === 'my-bookings' && <MyBookingsPanel state={state} />}

                {activeTab === 'my-purchases' && <MyPurchasesPanel state={state} setActiveTab={setActiveTab} />}

                {activeTab === 'edit-profile' && (
                    <div className="max-w-lg space-y-5">

                        {/* Display Name */}
                        <div className="border border-midnight-accent/20 bg-midnight-page/60 p-5 decorative-corner text-midnight-accent">
                            <label className="font-display text-midnight-accent text-xs block mb-3 font-semibold">Display Name</label>
                            <input type="text" value={profileForm.name}
                                onChange={(e) => setProfileForm({...profileForm, name: e.target.value})}
                                className="w-full bg-midnight-pageDark border border-midnight-accent/30 px-4 py-2.5 text-midnight-text font-serif text-sm focus:outline-none focus:border-midnight-accent transition-colors" />
                        </div>

                        {/* Email */}
                        <div className="border border-midnight-accent/20 bg-midnight-page/60 p-5 decorative-corner text-midnight-accent">
                            <label className="font-display text-midnight-accent text-xs block mb-3 font-semibold">Email Address</label>
                            <div className="flex gap-2 items-stretch">
                                <input type="email" value={state.user?.email || ''} disabled
                                    className="flex-grow bg-midnight-pageDark border border-midnight-accent/20 px-4 py-2.5 text-midnight-muted font-serif text-sm opacity-60 cursor-not-allowed" />
                                <button onClick={() => setEmailChangePending(true)}
                                    className="px-4 py-2.5 border border-midnight-accent/40 text-midnight-accent font-display text-sm hover:bg-midnight-accent/10 transition-colors whitespace-nowrap flex-shrink-0 font-semibold">
                                    Change Email
                                </button>
                            </div>
                            {emailChangePending && (
                                <p className="font-serif italic text-midnight-muted text-xs mt-2">This feature is coming soon.</p>
                            )}
                        </div>

                        {/* School */}
                        <div className="border border-midnight-accent/20 bg-midnight-page/60 p-5 decorative-corner text-midnight-accent">
                            <label className="font-display text-midnight-accent text-xs block mb-1 font-semibold">
                                School <span className="font-serif italic text-midnight-muted normal-case text-xs font-semibold">— optional</span>
                            </label>
                            <input type="text" value={profileForm.school}
                                placeholder="e.g. Hong Kong International School"
                                onChange={(e) => setProfileForm({...profileForm, school: e.target.value})}
                                className="w-full mt-2 bg-midnight-pageDark border border-midnight-accent/30 px-4 py-2.5 text-midnight-text font-serif text-sm focus:outline-none focus:border-midnight-accent transition-colors" />
                        </div>

                        {/* Year Group */}
                        <div className="border border-midnight-accent/20 bg-midnight-page/60 p-5 decorative-corner text-midnight-accent">
                            <label className="font-display text-midnight-accent text-xs block mb-3 font-semibold">Year Group</label>
                            <select value={profileForm.yearGroup}
                                onChange={(e) => setProfileForm({...profileForm, yearGroup: e.target.value})}
                                className="w-full bg-midnight-pageDark border border-midnight-accent/30 px-4 py-2.5 text-midnight-text font-serif text-sm focus:outline-none focus:border-midnight-accent transition-colors appearance-none">
                                <option value="">— select —</option>
                                <option value="year10">Year 10</option>
                                <option value="year11">Year 11</option>
                                <option value="year12">Year 12</option>
                                <option value="other">Other</option>
                            </select>
                        </div>

                        {/* Target Grade */}
                        <div className="border border-midnight-accent/20 bg-midnight-page/60 p-5 decorative-corner text-midnight-accent">
                            <label className="font-display text-midnight-accent text-xs block mb-3 font-semibold">Target IGCSE Grade</label>
                            <select value={profileForm.targetGrade}
                                onChange={(e) => setProfileForm({...profileForm, targetGrade: e.target.value})}
                                className="w-full bg-midnight-pageDark border border-midnight-accent/30 px-4 py-2.5 text-midnight-text font-serif text-sm focus:outline-none focus:border-midnight-accent transition-colors appearance-none">
                                <option value="">— select —</option>
                                <option value="Astar">A*</option>
                                <option value="A">A</option>
                                <option value="B">B</option>
                                <option value="C">C</option>
                            </select>
                        </div>

                        {/* Save */}
                        <div className="pt-2">
                            <button onClick={saveProfile}
                                className={`w-full py-3 font-display text-xs transition-all flex items-center justify-center gap-2 font-semibold ${profileSaved ? 'bg-green-900/40 border border-green-600/40 text-green-400 cursor-default' : 'bg-midnight-accent text-midnight-pageDark hover:bg-midnight-accentLight'}`}>
                                {profileSaved ? <><IconCheck className="w-4 h-4" /> Changes Saved</> : 'Save Changes'}
                            </button>
                        </div>

                    </div>
                )}
            </div>
            {/* Exam schedule picker modal */}
            {schedulePickerOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
                    onClick={() => setSchedulePickerOpen(false)}>
                    <div className="max-w-lg w-full bg-midnight-page border border-midnight-accent/60 shadow-2xl"
                        onClick={(e) => e.stopPropagation()}>
                        <div className="px-6 py-4 border-b border-midnight-accent/30 flex items-center justify-between">
                            <div>
                                <div className="font-display text-midnight-accent text-xs font-semibold">Exam Schedule</div>
                                <div className="font-serif italic text-midnight-muted text-xs">Choose a date to set your countdown target.</div>
                            </div>
                            <button onClick={() => setSchedulePickerOpen(false)} className="text-midnight-muted hover:text-midnight-accent p-1">
                                <IconX className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="max-h-[60vh] overflow-y-auto custom-scrollbar">
                            {state.examDates.length === 0 ? (
                                <div className="p-8 text-center font-serif italic text-midnight-muted">No exam dates configured yet.</div>
                            ) : (
                                state.examDates.map(ex => (
                                    <button key={ex.id}
                                        onClick={() => {
                                            state.setExamDate(ex.date);
                                            setTempExamDate(ex.date);
                                            setSchedulePickerOpen(false);
                                            setDateEditing(false);
                                        }}
                                        className="w-full text-left px-6 py-4 border-b border-midnight-accent/15 hover:bg-midnight-accent/10 transition-colors">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0 flex-grow">
                                                <div className="font-display text-midnight-text text-sm mb-0.5">{ex.label || ex.name}</div>
                                            </div>
                                            <div className="text-right flex-shrink-0">
                                                <div className="font-display text-midnight-accent text-sm">
                                                    {ex.date ? new Date(ex.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                                                </div>
                                                {ex.date && daysUntil(ex.date) >= 0 && (
                                                    <div className="font-serif italic text-midnight-muted text-xs">in {daysUntil(ex.date)} days</div>
                                                )}
                                            </div>
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccountPage;
